import React, { FC } from 'react';
import * as Styled from './Form.styles';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { useTranslations } from '@shared/modules/translation/hooks';
import { HttpError, HttpStatusCode } from '@core/http';
import { ForgotPasswordParams, forgotPasswordSchema } from '@modules/auth/model';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@styles/shared/form/Input';
import { HttpErrorMessage, HttpSuccessMessage } from '@shared/components/message/Message';
import { Translations } from '@shared/modules/translation/locales';

function getLabelBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.NOT_FOUND) return translations.forgotPassword.error.notFound;
        return null;
      },
    ),
  );
}

interface ForgotPasswordFormProps {
  loading: boolean;
  error: O.Option<HttpError>;
  onSubmit: (params: ForgotPasswordParams) => void;
  success: boolean;
}

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({ loading, error, onSubmit, success }) => {
  const translations = useTranslations();

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<ForgotPasswordParams>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const labelBuilder = getLabelBuilder(error, translations);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Styled.FormContainer>
        <h1>{translations.forgotPassword.title}</h1>

        <Styled.FormContent>
          <HttpErrorMessage error={error} labelBuilder={labelBuilder} />

          {success ? <HttpSuccessMessage labelBuilder={translations.forgotPassword.success} /> : null}

          <Styled.FormText>{translations.forgotPassword.text}</Styled.FormText>

          <Styled.FormInput label={translations.forgotPassword.email} htmlFor="email">
            <Input
              id="email"
              type="email"
              placeholder={translations.forgotPassword.emailPlaceholder}
              error={!!errors.email}
              disabled={success}
              {...register('email')}
            />
          </Styled.FormInput>

          <Styled.FormActions>
            <Styled.FormButton type="submit" $loading={loading} $disabled={success}>
              {translations.forgotPassword.send}
            </Styled.FormButton>

            <Styled.FormCancel to="sign-in">{translations.forgotPassword.cancel}</Styled.FormCancel>
          </Styled.FormActions>
        </Styled.FormContent>
      </Styled.FormContainer>
    </form>
  );
};

export default ForgotPasswordForm;
